import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Download } from "lucide-react";
import { API } from "@/lib/api";

export default function Form8Wizard() {
  const [fy, setFy] = useState("2025-26");
  const [d, setD] = useState({ fy: "2025-26", llpin: "", solvency_declaration: true,
    date_of_declaration: "", as_on_date: "2026-03-31", contribution_received: 0,
    total_assets: 0, total_liabilities: 0, turnover: 0, profit_before_tax: 0, profit_after_tax: 0,
    gross_income: 0, expenditure: 0, designated_partner_1: "", designated_partner_2: "" });

  const load = async (f) => {
    const r = await api.get("/mca/form-8", { params: { fy: f } });
    setD({ ...d, ...r.data, fy: f });
  };
  useEffect(() => { load(fy); /* eslint-disable-next-line */ }, [fy]);

  const save = async () => {
    try { await api.post("/mca/form-8", d); toast.success("Form 8 draft saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const exportJson = async () => {
    const res = await fetch(`${API}/mca/form-8.json?fy=${fy}`, { credentials: "include" });
    if (!res.ok) return toast.error("Save first, then export");
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `LLP-Form-8_${fy}.json`; a.click();
    URL.revokeObjectURL(url); toast.success("JSON downloaded");
  };
  const setF = (k, v) => setD({ ...d, [k]: v });

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="form8-page">
      <PageHeader eyebrow="Corporate Compliance" title="LLP Form 8 Wizard · Statement of Accounts & Solvency"
        description="LLP Act 2008 · Rule 24 · Filed on or before 30 October each year. Draft persists per FY; export MCA-compatible JSON." />
      <Card className="p-4 rounded-sm flex items-end gap-3">
        <div><Label>Financial Year</Label><Input value={fy} onChange={(e) => setFy(e.target.value)} className="rounded-sm mt-1 w-40" data-testid="f8-fy" /></div>
        <div><Label>LLPIN</Label><Input value={d.llpin || ""} onChange={(e) => setF("llpin", e.target.value.toUpperCase())} className="rounded-sm mt-1 font-mono w-56" data-testid="f8-llpin" placeholder="AAA-1234" /></div>
        <div className="flex-1" />
        <Button variant="outline" onClick={save} className="rounded-sm gap-1.5" data-testid="f8-save"><Save className="w-4 h-4" /> Save Draft</Button>
        <Button className="rounded-sm bg-accent text-white gap-1.5" onClick={exportJson} data-testid="f8-export"><Download className="w-4 h-4" /> Export JSON</Button>
      </Card>

      <Tabs defaultValue="a">
        <TabsList className="rounded-sm">
          <TabsTrigger value="a" className="rounded-sm">Part A · Solvency</TabsTrigger>
          <TabsTrigger value="b" className="rounded-sm">Part B · Statement of Accounts</TabsTrigger>
          <TabsTrigger value="c" className="rounded-sm">Part C · Income & Expenditure</TabsTrigger>
          <TabsTrigger value="cert" className="rounded-sm">Certification</TabsTrigger>
        </TabsList>
        <TabsContent value="a" className="mt-4">
          <Card className="p-5 rounded-sm space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!d.solvency_declaration} onChange={(e) => setF("solvency_declaration", e.target.checked)} data-testid="f8-solvent" />
              <div><div className="text-sm font-medium">The LLP is able to pay its debts in full as they fall due in the normal course of business</div><div className="text-[10px] text-muted-foreground">Statutory declaration u/r 24(8)</div></div>
            </label>
            <div><Label>Date of Declaration</Label><Input type="date" value={d.date_of_declaration || ""} onChange={(e) => setF("date_of_declaration", e.target.value)} className="rounded-sm mt-1 w-56" data-testid="f8-decl-date" /></div>
          </Card>
        </TabsContent>
        <TabsContent value="b" className="mt-4">
          <Card className="p-5 rounded-sm grid md:grid-cols-2 gap-3">
            <div><Label>As on Date</Label><Input type="date" value={d.as_on_date || ""} onChange={(e) => setF("as_on_date", e.target.value)} className="rounded-sm mt-1" data-testid="f8-as-on" /></div>
            <div><Label>Contribution Received (₹)</Label><Input type="number" value={d.contribution_received} onChange={(e) => setF("contribution_received", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="f8-contrib" /></div>
            <div><Label>Total Assets (₹)</Label><Input type="number" value={d.total_assets} onChange={(e) => setF("total_assets", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="f8-assets" /></div>
            <div><Label>Total Liabilities (₹)</Label><Input type="number" value={d.total_liabilities} onChange={(e) => setF("total_liabilities", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="f8-liab" /></div>
            <div><Label>Turnover (₹)</Label><Input type="number" value={d.turnover} onChange={(e) => setF("turnover", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="f8-turnover" /></div>
            <div><Label>Profit Before Tax (₹)</Label><Input type="number" value={d.profit_before_tax} onChange={(e) => setF("profit_before_tax", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="f8-pbt" /></div>
            <div><Label>Profit After Tax (₹)</Label><Input type="number" value={d.profit_after_tax} onChange={(e) => setF("profit_after_tax", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="f8-pat" /></div>
          </Card>
        </TabsContent>
        <TabsContent value="c" className="mt-4">
          <Card className="p-5 rounded-sm grid md:grid-cols-2 gap-3">
            <div><Label>Gross Income (₹)</Label><Input type="number" value={d.gross_income} onChange={(e) => setF("gross_income", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="f8-gross" /></div>
            <div><Label>Total Expenditure (₹)</Label><Input type="number" value={d.expenditure} onChange={(e) => setF("expenditure", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="f8-exp" /></div>
          </Card>
        </TabsContent>
        <TabsContent value="cert" className="mt-4">
          <Card className="p-5 rounded-sm grid md:grid-cols-2 gap-3">
            <div><Label>Designated Partner 1</Label><Input value={d.designated_partner_1 || ""} onChange={(e) => setF("designated_partner_1", e.target.value)} className="rounded-sm mt-1" data-testid="f8-dp1" placeholder="Name · DPIN" /></div>
            <div><Label>Designated Partner 2</Label><Input value={d.designated_partner_2 || ""} onChange={(e) => setF("designated_partner_2", e.target.value)} className="rounded-sm mt-1" data-testid="f8-dp2" placeholder="Name · DPIN" /></div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
